// The Reports period model, shared by the API (which adds the salon timezone and UTC instants) and the web app
// (period picker, "Oct 1–20 vs Sep 1–20" labels). Pure calendar math on salon-local `YYYY-MM-DD` dates — no
// timezones, no I/O — so it is DST-free and tested in isolation. Every window is inclusive on both ends.
// ---------------------------------------------------------------------------
import { addDaysIso, daysInclusive } from '../marketing/ad-spend-math';

/** Every period a Reports page can show, in picker order. */
export const REPORT_PERIODS = [
  'this-month',
  'last-month',
  'last-7-days',
  'last-30-days',
  'last-90-days',
  'this-year',
] as const;

export type ReportPeriod = (typeof REPORT_PERIODS)[number];

export const DEFAULT_REPORT_PERIOD: ReportPeriod = 'this-month';

/** An inclusive window of salon-local calendar dates (`YYYY-MM-DD`). */
export interface ReportDateWindow {
  fromIso: string;
  toIso: string;
}

export interface ReportPeriodDates {
  current: ReportDateWindow;
  /** The same-length window just before `current`, for every "vs" figure. */
  previous: ReportDateWindow;
  /** Calendar days in `current`. */
  dayCount: number;
  /**
   * Calendar days in `previous`. Can differ from `dayCount` (this month on Mar 31 vs Feb 1–28, last month March
   * vs February): insight rules compare per-day rates when the two differ, so a flat salon never reads "↑ 11%".
   */
  previousDayCount: number;
  /** True when `current` ends today, so its last day is still running ("so far"). */
  isPartial: boolean;
}

const LAST_DAYS: Partial<Record<ReportPeriod, number>> = {
  'last-7-days': 7,
  'last-30-days': 30,
  'last-90-days': 90,
};

/** A `?period=` value from the URL or a request; anything unknown falls back to the default. */
export function parseReportPeriod(raw: unknown): ReportPeriod {
  return typeof raw === 'string' && (REPORT_PERIODS as readonly string[]).includes(raw)
    ? (raw as ReportPeriod)
    : DEFAULT_REPORT_PERIOD;
}

function parts(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split('-').map(Number);
  return { year: year!, month: month!, day: day! };
}

function toIso(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Days in a 1-based month (UTC arithmetic: day 0 of the next month is this month's last day). */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** The month before `year-month` (1-based), as year + month. */
function monthBefore(year: number, month: number): { year: number; month: number } {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}

/**
 * The current and comparison windows for a period, given today's salon-local date.
 * - this-month: the 1st … today vs the same day numbers of last month (day 31 → 30 / 28 / 29 when shorter).
 * - last-month: the whole of last month vs the whole month before it.
 * - last-N-days: the N days ending today (the analytics `7d/30d/90d` presets) vs the N days before them.
 * - this-year: Jan 1 … today vs the same dates last year (Feb 29 → Feb 28).
 */
export function resolveReportPeriodDates(period: ReportPeriod, todayIso: string): ReportPeriodDates {
  const today = parts(todayIso);
  const lastDays = LAST_DAYS[period];
  let current: ReportDateWindow;
  let previous: ReportDateWindow;

  if (lastDays) {
    current = { fromIso: addDaysIso(todayIso, -(lastDays - 1)), toIso: todayIso };
    previous = { fromIso: addDaysIso(todayIso, -(2 * lastDays - 1)), toIso: addDaysIso(todayIso, -lastDays) };
  } else if (period === 'this-year') {
    const lastYear = today.year - 1;
    current = { fromIso: toIso(today.year, 1, 1), toIso: todayIso };
    previous = {
      fromIso: toIso(lastYear, 1, 1),
      toIso: toIso(lastYear, today.month, Math.min(today.day, daysInMonth(lastYear, today.month))),
    };
  } else if (period === 'last-month') {
    const last = monthBefore(today.year, today.month);
    const before = monthBefore(last.year, last.month);
    current = { fromIso: toIso(last.year, last.month, 1), toIso: toIso(last.year, last.month, daysInMonth(last.year, last.month)) };
    previous = { fromIso: toIso(before.year, before.month, 1), toIso: toIso(before.year, before.month, daysInMonth(before.year, before.month)) };
  } else {
    const last = monthBefore(today.year, today.month);
    current = { fromIso: toIso(today.year, today.month, 1), toIso: todayIso };
    previous = {
      fromIso: toIso(last.year, last.month, 1),
      toIso: toIso(last.year, last.month, Math.min(today.day, daysInMonth(last.year, last.month))),
    };
  }

  return {
    current,
    previous,
    dayCount: daysInclusive(current.fromIso, current.toIso),
    previousDayCount: daysInclusive(previous.fromIso, previous.toIso),
    isPartial: current.toIso === todayIso,
  };
}
